export type Programme = {
  slug: string;
  band: string;
  ageRange: string;
  title: string;
  summary: string;
  cefr: string;
  duration: string;
  workload: string;
  cohort: string;
  spots: string;
  outcomes: string[];
  metaTitle: string;
  metaDescription: string;
  intro: string;
};

export const programmes: Programme[] = [
  {
    slug: "early-phonics-explorers",
    band: "Ages 4–6",
    ageRange: "4-6",
    title: "Early Phonics Explorers",
    summary:
      "Live online English classes for kids aged 4–6: synthetic phonics, first words and speaking confidence in a micro-cohort of five.",
    cefr: "Pre-A1",
    duration: "30-minute lessons",
    workload: "PT30M",
    cohort: "Max 5 children",
    spots: "Max 5 children per group",
    outcomes: [
      "All 44 English phonemes recognised and produced",
      "Blends and reads simple CVC words aloud",
      "Answers everyday questions in full short sentences",
      "Confident singing, storytelling and turn-taking",
    ],
    metaTitle:
      "Early Phonics Explorers: English Course for Ages 4-6 | Smart Word Labs",
    metaDescription:
      "Live online English classes for kids ages 4-6. Synthetic phonics, first words and speaking confidence in 30-minute micro-cohort lessons. Book a free assessment.",
    intro:
      "Early Phonics Explorers is where reading starts. Over short, high-energy 30-minute lessons, children meet every English sound, learn to blend them into words, and practise speaking out loud from the very first session — all in a group of no more than five so nobody hides at the back.",
  },
  {
    slug: "literacy-builders",
    band: "Ages 7–9",
    ageRange: "7-9",
    title: "Literacy Builders",
    summary:
      "Small group English tutoring for children aged 7–9 covering reading fluency, structured writing and everyday conversation, aligned to CEFR A1–A2.",
    cefr: "A1 – A2",
    duration: "45-minute lessons",
    workload: "PT45M",
    cohort: "Max 5 children",
    spots: "Max 5 children per group",
    outcomes: [
      "Reads age-appropriate texts with fluency and expression",
      "Writes structured paragraphs with accurate punctuation",
      "Holds a two-minute spoken exchange on familiar topics",
      "Builds an active vocabulary of 1,500+ words",
    ],
    metaTitle: "Literacy Builders: English Course for Ages 7-9 | Smart Word Labs",
    metaDescription:
      "Small group English tutoring for children ages 7-9. Reading fluency, structured writing and confident conversation, mapped to CEFR A1-A2. Book a free assessment.",
    intro:
      "Literacy Builders takes children from decoding words to genuinely enjoying them. Lessons balance reading aloud, structured writing and conversation, so progress at school and confidence in conversation move together — tracked against CEFR A1 to A2 benchmarks.",
  },
  {
    slug: "confident-communicators",
    band: "Ages 10–12",
    ageRange: "10-12",
    title: "Confident Communicators",
    summary:
      "A CEFR English course for children aged 10–12 building essay writing, debate and interview skills ready for secondary school.",
    cefr: "A2 – B1",
    duration: "45-minute lessons",
    workload: "PT45M",
    cohort: "Max 5 children",
    spots: "Max 5 children per group",
    outcomes: [
      "Presents and defends an opinion with supporting reasons",
      "Writes essays, emails and creative narratives",
      "Handles interview-style questions with poise",
      "Prepared for secondary school and Cambridge-style assessment",
    ],
    metaTitle:
      "Confident Communicators: English Course for Ages 10-12 | Smart Word Labs",
    metaDescription:
      "Live English lessons for kids ages 10-12 covering essay writing, debate and interview technique, aligned to CEFR A2-B1. Book a free assessment today.",
    intro:
      "Confident Communicators prepares older children for the demands of secondary school. They debate, present, and write at length — then get precise, immediate feedback in a cohort small enough that every child speaks in every lesson.",
  },
  {
    slug: "ielts-mastery",
    band: "All ages",
    ageRange: "0-99",
    title: "IELTS Mastery 1-on-1",
    summary:
      "Private IELTS coaching for Academic or General Training candidates of any age. One-on-one sessions built around your target band score and exam date.",
    cefr: "B1 – C2",
    duration: "45-minute lessons",
    workload: "PT45M",
    cohort: "One-on-one",
    spots: "Private 1-on-1",
    outcomes: [
      "Understand the IELTS Academic & General Training format and scoring criteria",
      "Write Task 1 and Task 2 responses that hit the target band descriptors",
      "Speak fluently under mock-exam conditions with examiner-style feedback",
      "Apply time-management and error-analysis strategies in Reading & Listening",
    ],
    metaTitle:
      "IELTS Mastery 1-on-1: Academic & General Training Coaching | Smart Word Labs",
    metaDescription:
      "Private online IELTS coaching for Academic or General Training. All ages welcome. Targeted band-score strategy, mock tests and writing feedback with Ms. Salma.",
    intro:
      "IELTS Mastery 1-on-1 is for anyone preparing for the IELTS Academic or General Training test — there is no age limit. Each 45-minute session is private, intensive and shaped around your target band score and exam date, with direct feedback from Ms. Salma on every skill area.",
  },
];

export function getProgramme(slug: string) {
  return programmes.find((p) => p.slug === slug);
}
